<template>
    <div class="tw:w-full">
        <BaseMultiselect
            label="Provincia"
            name="provincias"
            :value="selectedId"
            :options="provincias"
            label-key="nombre"
            value-key="id"
            :disabled="disabled"
            rules="required"
            @input="onSelect"
        />
    </div>
</template>

<script setup>
    import { ref, watch, onMounted } from 'vue';
    import store from '@/store';
    import { config } from '@/config';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';

    const props = defineProps({
        modelValue: { type: [Number, Object, String], default: null },
        disabled: { type: Boolean, default: false },
        pais: { type: Number, default: 68 }
    });

    const emit = defineEmits(['update:modelValue', 'provinciasCargadas']);

    const provincias = ref([]);
    const selectedId = ref(null);

    // Guard null-safe para un modelValue "objeto con id". La forma anterior
    // (`typeof val === 'object' && val !== null && val.id`) tenía dos defectos:
    // comparaba `val` con null tras estrechar por typeof, y usaba el `id` como
    // booleano, así que un id=0 se descartaba. `val && typeof val === 'object'`
    // cubre null sin compararlo explícitamente y `'id' in val` acepta el 0.
    function isObjectWithId(val) {
        return Boolean(val) && typeof val === 'object' && !Array.isArray(val) && 'id' in val;
    }

    function syncSelected(val) {
        if (!val || !provincias.value.length) {
            selectedId.value = null;
            return;
        }
        let found = null;
        if (typeof val === 'number') {
            found = provincias.value.find(p => p.id === val);
        } else if (isObjectWithId(val)) {
            found = provincias.value.find(p => p.id === val.id);
        } else if (typeof val === 'string') {
            found = provincias.value.find(p => p.nombre === val);
        }
        selectedId.value = found ? found.id : null;
    }

    async function fetchProvincias() {
        if (!props.pais) return;
        try {
            const res = await store.dispatch(
                config.getVuexAlias('select.provincias'),
                { filters: { pais_id: props.pais } }
            );
            provincias.value = res.data.data || res.data || [];
            emit('provinciasCargadas', provincias.value);
            syncSelected(props.modelValue);
        } catch (err) {
            console.error('Error fetching provinces:', err);
        }
    }

    function onSelect(val) {
        const found = provincias.value.find(p => p.id === val);
        selectedId.value = val;
        emit('update:modelValue', found || val);
    }

    watch(() => props.modelValue, newVal => {
        syncSelected(newVal);
    });

    watch(() => props.pais, () => {
        selectedId.value = null;
        fetchProvincias();
    });

    onMounted(() => {
        fetchProvincias();
    });
</script>
